// ============================================================================
// InlineNotice — compact notice for a condition the user should act on
// ============================================================================
// A pill-sized notice that sits INSIDE an existing row of the screen (a filter
// or context bar, the header's custom toolbar slot) — never on a row of its
// own, so it takes no extra screen height. Keep `text` short; the full
// explanation goes in `detail`, shown as the tooltip and read by screen
// readers. Render nothing when there is nothing to report.
//
// Example (a context bar with free space on the right):
//   <div className="filter-bar">
//     ...filters...
//     {gap && (
//       <InlineNotice
//         tone="warning"
//         text="Meal Plan: no tax rule from 01 Apr 2027"
//         detail="Bookings with meals checking in on or after 01 Apr 2027 will be refused until a rule is added."
//         action={{ label: 'Add rule', onClick: openAdd }}
//       />
//     )}
//   </div>
// ============================================================================

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import './guidance.css';

export type InlineNoticeTone = 'info' | 'warning' | 'danger';

interface Props {
  tone: InlineNoticeTone;
  /** Short text shown in the pill; truncated with an ellipsis when space runs out. */
  text: string;
  /** Full explanation — tooltip and accessible description. */
  detail?: string;
  action?: { label: string; onClick: () => void };
  className?: string;
}

export const InlineNotice: React.FC<Props> = ({ tone, text, detail, action, className = '' }) => {
  const Icon = tone === 'info' ? IconSystem.info : IconSystem.warning;
  return (
    <div
      className={`es-inline-notice es-tone-${tone} ${className}`.trim()}
      role={tone === 'info' ? 'status' : 'alert'}
      title={detail ? `${text}. ${detail}` : text}
    >
      <Icon size={15} />
      <span className="es-inline-notice-text">{text}</span>
      {detail && <span style={VISUALLY_HIDDEN}>{detail}</span>}
      {action && (
        <button type="button" className="es-inline-notice-action" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  );
};

const VISUALLY_HIDDEN: React.CSSProperties = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0,
};

export default InlineNotice;
